import * as ramda from 'ramda';

import ErrorStatus from '$shared/constants/ErrorStatus';
import UIErrorMessage from '$shared/constants/UIErrorMessage';
import { ErrorMessage } from '$shared/models/ErrorMessage';
import type ApiError from '$shared/models/api/ApiError';

export type ErrorStatusMapping = Partial<Record<ErrorStatus, ErrorMessage>>;

export class ErrorHandler {
  static getMessage(
    status: ErrorStatus,
    error: ApiError | Error,
    statusMapping?: ErrorStatusMapping
  ): ErrorMessage {
    let errorMessage: ErrorMessage | undefined = ramda.clone(statusMapping?.[status]);
    // If error message is not available then we need to create a error object based on status.
    if (!errorMessage) {
      switch (status) {
        case ErrorStatus.UnAuthourized: {
          errorMessage = ErrorMessage.create(UIErrorMessage.AuthenticationExpired);
          break;
        }
        case ErrorStatus.ServerError: {
          errorMessage = ErrorMessage.forServerError();
          break;
        }
        case ErrorStatus.GatewayTimeoutError:
        case ErrorStatus.MethodNotAllowed: {
          errorMessage = ErrorMessage.create(UIErrorMessage.ServerError, false);
          break;
        }
        case ErrorStatus.UnHandledException: {
          errorMessage = ErrorMessage.create(error?.message || UIErrorMessage.UnknowError, true);
          break;
        }
        case ErrorStatus.Forbidden: {
          errorMessage = ErrorMessage.create(UIErrorMessage.ActionForbidden, true);
          break;
        }
        case ErrorStatus.ContentTooLarge:
        case ErrorStatus.URLTooLong: {
          errorMessage = ErrorMessage.create(error?.message || UIErrorMessage.ContentOrURITooLong);
          break;
        }
        default: {
          errorMessage = ErrorMessage.create(error?.message || 'Unknown error', true);
        }
      }
    } else {
      if (status === ErrorStatus.Forbidden) errorMessage.setSendToSentry(!!error?.message);
    }
    errorMessage?.setMessage(error?.message);

    error.errors && errorMessage?.rewriteTitle(Object.keys(error.errors)?.first());
    // By default sendtoSentry flag is true, then need to check the status and set send to sentry flag, based on error status,
    // if error status is greater than or equal 500,
    // set the value to false, no need to log the 500 or above status to sentry
    errorMessage?.sendToSentry && errorMessage?.setSendToSentry(status < ErrorStatus.ServerError);
    return errorMessage;
  }
}
